import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth, useDb } from '../firebase/FirebaseContext';
import { updateHolidayPrefs } from '../callables';
import type { TheaWebUser } from '../schemas/theaWebUser';

const LOAD_ERROR = 'Couldn’t load your settings. Please reload to try again.';
const SAVE_ERROR = 'Couldn’t save your settings. Please try again.';

/** Private user-doc state only. Never log payloads, errors, or holiday choices. */
export function useHolidayPrefs() {
  const auth = useAuth();
  const db = useDb();
  const [hiddenHolidays, setHidden] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scope = useRef({ uid: '', pending: false, stored: [] as string[] });

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const stopAuth = onAuthStateChanged(auth, (user) => {
      unsubscribe?.();
      const current = { uid: user && !user.isAnonymous ? user.uid : '', pending: false, stored: [] as string[] };
      scope.current = current;
      setHidden([]);
      setSaving(false);
      setError(null);
      setSignedIn(Boolean(current.uid));
      setReady(!current.uid);
      if (!current.uid) return;
      unsubscribe = onSnapshot(doc(db, 'theaWebUser', current.uid), (snapshot) => {
        if (scope.current !== current) return;
        const data = snapshot.data() as TheaWebUser | undefined;
        current.stored = data?.hiddenHolidays ?? [];
        if (!current.pending) setHidden(current.stored);
        setReady(true);
        setError(null);
      }, () => {
        if (scope.current !== current) return;
        setReady(false);
        setError(LOAD_ERROR);
      });
    });
    return () => {
      stopAuth();
      unsubscribe?.();
      scope.current = { uid: '', pending: false, stored: [] };
    };
  }, [auth, db]);

  const save = async (hidden: string[]) => {
    const current = scope.current;
    if (!ready || !current.uid || current.pending || auth.currentUser?.uid !== current.uid || auth.currentUser.isAnonymous) return;
    current.pending = true;
    setSaving(true);
    setError(null);
    setHidden(hidden);
    try {
      const result = await updateHolidayPrefs({ hiddenHolidays: hidden });
      if (!result.data.success) throw new Error(SAVE_ERROR);
      if (scope.current !== current) return;
      current.stored = result.data.hiddenHolidays;
      setHidden(current.stored);
    } catch {
      if (scope.current !== current) return;
      setHidden(current.stored);
      setError(SAVE_ERROR);
    } finally {
      if (scope.current === current) {
        current.pending = false;
        setSaving(false);
      }
    }
  };

  return { hiddenHolidays, ready, signedIn, saving, error, save };
}
